import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type { FC } from 'react';
import { useIntl } from 'react-intl';

import type {
  Country,
  GpsCoordinates,
} from '@xing-com/crate-common-graphql-types';
import {
  trackContactOpenMaps,
  trackContactClickPhoneNumber,
  trackContactClickEmail,
  trackContactClickWebpage,
} from '@xing-com/crate-entity-pages-common/tracking';
import { useHost } from '@xing-com/crate-xinglet';
import { BodyCopy } from '@xing-com/typography';

import { Map } from '../map/map';

import * as Styled from './location.styles';

type PhoneNumber = {
  countryCode: number;
  number: string;
};

const buildContactNumber = (countryCode: number, number: string) =>
  parsePhoneNumberFromString(
    `+${countryCode}${number}`
  )?.formatInternational() || '';

type LocationProps = {
  trackingModuleType: string;
  label: string;
  city: string;
  address: string;
  postcode: string;
  country?: Nullable<Omit<Country, 'provinces'>>;
  coordinates?: Nullable<GpsCoordinates>;
  email?: string;
  phoneNumber?: Nullable<PhoneNumber>;
  faxNumber?: Nullable<PhoneNumber>;
  websiteURL?: Nullable<string>;
  intersected?: boolean;
};
export const Location: FC<LocationProps> = ({
  trackingModuleType,
  label,
  city,
  address,
  postcode,
  country,
  coordinates,
  email,
  phoneNumber,
  faxNumber,
  websiteURL,
  intersected,
}) => {
  const { $t } = useIntl();
  const { isServer } = useHost();

  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${address}, ${postcode} ${city}, ${country?.localizationValue}`
  )}`;

  const countryCopy = country ? $t({ id: country.localizationKey }) : '';

  return (
    <Styled.MapsWrapper>
      {!isServer && intersected && coordinates && (
        <Map lat={coordinates?.latitude} lng={coordinates?.longitude} />
      )}
      <Styled.MapsButton
        variant="secondary"
        size="small"
        href={mapsHref}
        onClick={() => trackContactOpenMaps()}
        target="_blank"
        data-cy="open_maps_btn"
        data-testid="locations-map-external"
        rel="nofollow"
      >
        {$t({ id: 'EP_LOCATIONS_OPEN_MAP' })}
      </Styled.MapsButton>

      <Styled.AddressWrapper data-testid="locations-address-card">
        <Styled.MapHeadline fontWeight="bold" lineHeight="dense" size="xlarge">
          {label}
        </Styled.MapHeadline>
        <Styled.Address size="small">{`${address}, ${postcode} ${city}, ${countryCopy}`}</Styled.Address>
        <div className="allContactsWrapper">
          <Styled.ContactNumbers>
            {phoneNumber ? (
              <Styled.LinkableContactWrapper>
                <Styled.PhoneButton
                  size="small"
                  onClick={() =>
                    trackContactClickPhoneNumber(trackingModuleType)
                  }
                  href={`tel:+${phoneNumber.countryCode}${phoneNumber.number}`}
                >
                  {`${$t({ id: 'EP_TELEPHONE' })}: ${buildContactNumber(
                    phoneNumber.countryCode,
                    phoneNumber.number
                  )}`}
                </Styled.PhoneButton>
              </Styled.LinkableContactWrapper>
            ) : null}
            {faxNumber ? (
              <BodyCopy noMargin size="small">
                {`${$t({ id: 'EP_FAX' })}: ${buildContactNumber(
                  faxNumber.countryCode,
                  faxNumber.number
                )}`}
              </BodyCopy>
            ) : null}
          </Styled.ContactNumbers>
          {email ? (
            <Styled.LinkableContactWrapper>
              <Styled.WebContactsButton
                size="small"
                href={`mailto:${email}`}
                onClick={() => trackContactClickEmail(trackingModuleType)}
              >
                {email}
              </Styled.WebContactsButton>
            </Styled.LinkableContactWrapper>
          ) : null}
          {websiteURL ? (
            <Styled.WebsiteLink>
              <Styled.WebContactsButton
                size="small"
                target="_blank"
                href={websiteURL}
                onClick={() => trackContactClickWebpage(trackingModuleType)}
                rel="nofollow"
              >
                {websiteURL}
              </Styled.WebContactsButton>
            </Styled.WebsiteLink>
          ) : null}
        </div>
      </Styled.AddressWrapper>
    </Styled.MapsWrapper>
  );
};
